import { useState } from 'react';

import { Link, useNavigate } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Separator,
} from '@fade/ui';

import { useSession } from '../lib/auth';
import { useCheckInDms, useDms, useUpdateDms } from '../lib/queries/dms';
import { useDeleteLegacy, useMyLegacies } from '../lib/queries/legacies';

export function ProfilePage() {
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } = useSession();

  if (sessionPending) {
    return (
      <main className="mx-auto max-w-3xl px-6 py-16">
        <p className="text-muted-foreground">Loading…</p>
      </main>
    );
  }

  if (!session) {
    void navigate({ to: '/sign-in', search: { redirect: '/profile' } });
    return null;
  }

  return (
    <main className="mx-auto max-w-3xl px-6 py-16">
      <header className="mb-8">
        <h1 className="font-serif text-3xl font-light">{session.user.name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{session.user.email}</p>
      </header>

      <div className="space-y-10">
        <MyStoriesSection />
        <Separator />
        <DmsSection />
        <Separator />
        <SignOutSection />
      </div>
    </main>
  );
}

function MyStoriesSection() {
  const { data, isPending } = useMyLegacies();
  const del = useDeleteLegacy();

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-medium">Your stories</h2>
        <Link to="/legacies/new">
          <Button>Write a new one</Button>
        </Link>
      </div>

      {isPending ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : !data || data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No stories yet.</p>
      ) : (
        <div className="space-y-2">
          {data.items.map((row) => (
            <Card key={row.id}>
              <CardHeader>
                <CardTitle>
                  <Link
                    to={row.status === 'draft' ? '/legacies/$id/edit' : '/legacies/$id'}
                    params={{ id: row.id }}
                    className="hover:underline"
                  >
                    {row.title}
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  {row.status}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => del.mutate(row.id)}
                  disabled={del.isPending}
                >
                  Delete
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

function DmsSection() {
  const { data, isPending } = useDms();
  const update = useUpdateDms();
  const checkIn = useCheckInDms();

  const [interval, setInterval] = useState('');
  const [enabled, setEnabled] = useState(false);

  if (isPending || !data) {
    return (
      <section>
        <h2 className="font-serif text-xl font-medium">Dead Man Switch</h2>
        <p className="mt-2 text-muted-foreground">Loading…</p>
      </section>
    );
  }

  return (
    <section id="dms" className="space-y-4">
      <h2 className="font-serif text-xl font-medium">Dead Man Switch</h2>
      <p className="text-sm text-muted-foreground">
        If enabled, Fade will email you every <strong>{data.intervalDays} days</strong> to confirm you are
        still here. If you do not respond for two intervals, your account is marked inactive.
      </p>

      {data.lastCheckInAt ? (
        <p className="text-xs text-muted-foreground">
          Last checked in {new Date(data.lastCheckInAt).toLocaleString()}
          {data.daysSinceCheckIn !== null ? ` (${data.daysSinceCheckIn} days ago)` : ''}.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">Never checked in.</p>
      )}

      <form
        className="space-y-3"
        onSubmit={async (e) => {
          e.preventDefault();
          await update.mutateAsync({
            enabled,
            intervalDays: Number.parseInt(interval, 10) || data.intervalDays,
          });
        }}
      >
        <div className="space-y-1">
          <Label htmlFor="interval">Check-in interval (days)</Label>
          <Input
            id="interval"
            type="number"
            min={1}
            max={365}
            value={interval || String(data.intervalDays)}
            onChange={(e) => setInterval(e.target.value)}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Send me reminders
        </label>
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
      </form>

      <Button
        variant="secondary"
        onClick={() => checkIn.mutate()}
        disabled={checkIn.isPending}
      >
        {checkIn.isPending ? 'Checking in…' : 'Check in now'}
      </Button>
    </section>
  );
}

function SignOutSection() {
  return (
    <section>
      <Button
        variant="secondary"
        onClick={async () => {
          await fetch('/api/auth/sign-out', {
            method: 'POST',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({}),
          });
          window.location.href = '/';
        }}
      >
        Sign out
      </Button>
    </section>
  );
}